import { forwardRef, type ChangeEvent } from 'react';
import { formatAadhaar } from '@/lib/forms';
import { Input, type InputProps } from './Input';

export type AadhaarInputProps = Omit<InputProps, 'inputMode' | 'maxLength' | 'type'>;

/**
 * An Aadhaar number: digits only, twelve at most, grouped in fours as typed.
 *
 * No `maxLength`: the browser would cut a pasted, spaced number short before
 * the digits were picked out; `formatAadhaar` is the cap.
 *
 * Re-grouping rewrites the value, which would throw the caret to the end on
 * every keystroke — so the caret is put back after the same number of digits
 * it followed, and editing the middle of the number stays where it is.
 *
 * Works with `register()` as a plain input does: the formatted value is
 * written back into the event before the caller's `onChange` sees it.
 */
export const AadhaarInput = forwardRef<HTMLInputElement, AadhaarInputProps>(function AadhaarInput(
  { onChange, placeholder = 'XXXX XXXX XXXX', ...props },
  ref,
) {
  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const caret = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = input.value.slice(0, caret).replace(/\D/g, '').length;

    const formatted = formatAadhaar(input.value);
    input.value = formatted;

    // Each full group of four before the caret has gained a space after it.
    const position = Math.min(
      digitsBeforeCaret + Math.floor(Math.max(digitsBeforeCaret - 1, 0) / 4),
      formatted.length,
    );
    input.setSelectionRange(position, position);

    onChange?.(event);
  }

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      className="font-mono"
      onChange={handleChange}
      {...props}
    />
  );
});
